import { useCallback } from 'react';
import API from '@api';
import { DEFAULT_MAP_CENTER } from '@constants/geo';
import { notifications } from '@utils/notifications';
import type { AddressFormFields } from '@customHooks/useAddressForm';
import type { UserStoreType } from '@store/UserStore';
import type { storeAddressT } from '@store/UserStore.type';
import type {
  PeopleAddressInput,
  AddPeopleAddressMutation,
  UpdatePeopleAddressMutation,
  MeQuery
} from '@schema/main';
import type { PrepareAllCommentsOptionsReturnType } from '@utils/orderUtils/prepareAllCommentsOptions';
import type { CommentsItemInput } from '@schema/cart';

interface UseAddressSaveOptions {
  userStore: UserStoreType;
  fields: AddressFormFields;
  setFields: (partial: Partial<AddressFormFields>) => void;
  onAfterSave?: (
    address: AddPeopleAddressMutation | UpdatePeopleAddressMutation
  ) => void;
  onAfterRemove?: (res: NonNullable<MeQuery['me']>['addresses']) => void;
  onAfterSelect?: (address: storeAddressT) => void;
}

export interface AddressSaveReturn {
  selectAddress: (address: storeAddressT) => void;
  saveAddress: () => void;
  removeAddress: (
    address: storeAddressT | null,
    e?: React.MouseEvent
  ) => () => void;
  isDisabledBtnSaveAddress: (state?: Partial<AddressFormFields>) => boolean;
  setAddressComments: (
    addressId: number,
    commentsData?: Partial<PrepareAllCommentsOptionsReturnType>
  ) => Promise<any>;
  prepareCommentsItems: (item: any) => CommentsItemInput;
}

const isValidPhone = (phone: string): boolean =>
  (phone.includes('+7 (') && phone.length === 18) ||
  (!phone.includes('+7 (') && phone.length === 10);

export const useAddressSave = ({
  userStore,
  fields,
  setFields,
  onAfterSave,
  onAfterRemove,
  onAfterSelect
}: UseAddressSaveOptions): AddressSaveReturn => {
  const prepareCommentsItems = useCallback((item: any): CommentsItemInput => {
    let value = '';
    let optionId: number | undefined;

    if (item.type === 'checkbox') {
      value = item.value ? '1' : '0';
    } else if (item.type === 'select') {
      optionId = item.value;
    } else if (item.type === 'input') {
      value = item.value;
    }

    return { commentId: item.id, value, optionId };
  }, []);

  const setAddressComments = useCallback(
    async (
      addressId: number,
      commentsData?: Partial<PrepareAllCommentsOptionsReturnType>
    ) => {
      const data = commentsData || {};
      const { selects = [], checkboxes = [], orderHome } = data;

      const commentsItems: CommentsItemInput[] = [];

      if (orderHome?.id) {
        commentsItems.push({
          commentId: orderHome.id,
          value: orderHome.value ? '1' : '0'
        });
      }

      checkboxes
        ?.filter((item: any) => item.attachment === 'address')
        .forEach(item => {
          commentsItems.push(prepareCommentsItems(item));
        });

      selects
        ?.filter((item: any) => item.attachment === 'address')
        .forEach(item => {
          const prepared = prepareCommentsItems(item);
          if (prepared.optionId) commentsItems.push(prepared);
        });

      return await API.addCommentsToAdress({
        id: +addressId,
        comment: { commentsItems }
      });
    },
    [prepareCommentsItems]
  );

  const selectAddress = useCallback(
    (address: storeAddressT) => {
      const coords =
        address?.dadata?.geoLat && address?.dadata?.geoLon
          ? [Number(address.dadata.geoLat), Number(address.dadata.geoLon)]
          : DEFAULT_MAP_CENTER;

      setFields({
        selectedAddress: address?.id ?? null,
        coords
      });

      onAfterSelect?.(address);
    },
    [setFields, onAfterSelect]
  );

  const isDisabledBtnSaveAddress = useCallback(
    (state?: Partial<AddressFormFields>) => {
      const checkState = state || fields;
      const { selectedAddress, phone, dadataId, balloonState } = checkState;

      let isDisabled = false;

      if (selectedAddress) {
        if (
          !dadataId ||
          (balloonState && Object.keys(balloonState).length > 0)
        ) {
          isDisabled = true;
        }
      } else {
        if (
          !dadataId ||
          !phone ||
          (phone.includes('+7 (') && phone.length !== 18)
        ) {
          isDisabled = true;
        }
      }

      return isDisabled;
    },
    [fields]
  );

  const saveAddress = useCallback(() => {
    const {
      phone,
      fio,
      address,
      dadataId,
      flat,
      comment,
      selectedAddress,
      isAddingNewAddress,
      isMainAddress
    } = fields;

    if (address && !isDisabledBtnSaveAddress() && isValidPhone(phone)) {
      const { user, addPeopleAddress, updatePeopleAddress } = userStore;

      const defaultData = {
        dadata: dadataId,
        flat,
        phone: phone || user?.phone,
        fio: fio || user?.fio,
        comment: comment || '',
        primary: isMainAddress,
        coords: null,
        confirmedByUser: true
      };

      if (!isAddingNewAddress && selectedAddress) {
        const data: PeopleAddressInput = {
          ...defaultData,
          id: Number(selectedAddress)
        };
        updatePeopleAddress(data).then((res: UpdatePeopleAddressMutation) => {
          onAfterSave?.(res);
        });
      } else {
        addPeopleAddress({ ...defaultData, primary: false }).then(
          (res: AddPeopleAddressMutation) => {
            onAfterSave?.(res);
          }
        );
      }

      setFields({
        isAddingNewAddress: false,
        isAddAddress: false
      });
    } else {
      notifications(
        'Проверьте правильность введенного номера телефона и адреса доставки!',
        'Внимание'
      );
    }
  }, [fields, userStore, isDisabledBtnSaveAddress, setFields, onAfterSave]);

  const removeAddress = useCallback(
    (address: storeAddressT | null, e?: React.MouseEvent) => () => {
      if (e) {
        e.stopPropagation();
      }

      if (address?.id) {
        userStore.removeAddress(address.id).then(res => {
          onAfterRemove?.(res?.addresses || []);
        });
      } else if (fields.selectedAddress) {
        userStore.removeAddress(Number(fields.selectedAddress)).then(res => {
          onAfterRemove?.(res?.addresses || []);
        });
      } else {
        const primaryAddress = userStore.presortedAddresses.find(
          (item: storeAddressT) => item.primary
        );
        const coords =
          primaryAddress?.dadata?.geoLat && primaryAddress?.dadata?.geoLon
            ? [primaryAddress.dadata.geoLat, primaryAddress.dadata.geoLon]
            : DEFAULT_MAP_CENTER;

        setFields({
          isAddAddress: false,
          isAddingNewAddress: false,
          selectedAddress: primaryAddress?.id
            ? Number(primaryAddress.id)
            : null,
          coords
        });
      }
    },
    [fields.selectedAddress, userStore, setFields, onAfterRemove]
  );

  return {
    selectAddress,
    saveAddress,
    removeAddress,
    isDisabledBtnSaveAddress,
    setAddressComments,
    prepareCommentsItems
  };
};

export default useAddressSave;
